// Public student registration page – accessible without authentication via the QR code link
import { useState, useEffect, useMemo } from 'react'
import { Loader2, XCircle, CheckCircle2, AlertTriangle } from 'lucide-react'

import { useIsMounted } from '@/core/hooks/useIsMounted'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/card'
import { Button } from '@/ui/button'
import { getCoursePassingScore, getCourseQuestions } from '@/features/classes/stores'
import { fetchCourseBySlug } from '@/core/api/coursesService'
import { cn } from '@/core/lib/utils'

import { getSessionByQrToken } from '../api/sessionsService'
import { createAttendee } from '../api/attendeesService'
import { buildFallbackCourse } from '../lib/publicCourseFallback'
import type { ExistingAttendee, PBSession } from '../api/sessionsService'
import type { AttendeeStatus, Course } from '../stores'

interface Props {
  token: string
  email?: string
  embedded?: boolean
}

type Step = 'loading' | 'error' | 'register' | 'quiz' | 'done'

const stepOrder: Array<'register' | 'quiz' | 'done'> = ['register', 'quiz', 'done']

/** Resolve the course from the public API; fall back to session-derived data
 *  (no diagnostic quiz) when the catalog cannot be fetched. */
async function resolveCourse(session: PBSession): Promise<Course> {
  try {
    const apiCourse = await fetchCourseBySlug(session.courseSlug ?? session.courseId)
    if (apiCourse) {return apiCourse}
  } catch {
    // Offline or catalog unavailable — degrade to the session-derived fallback
  }
  return buildFallbackCourse(session)
}

// Dashboard brand colors: navy #1b2a4a + orange #f5821f
const darkInputClass =
  'h-10 w-full rounded-md border border-slate-150 bg-white px-3 text-sm text-navy outline-none ring-0 placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20'
const primaryButtonClass =
  'h-10 rounded-lg bg-primary font-semibold text-white hover:bg-primary-hover transition-colors'
const progressDotActiveClass = 'w-6 bg-primary'
const progressDotInactiveClass = 'w-2 bg-slate-150'
const progressConnectorActiveClass = 'bg-primary/50'
const progressConnectorInactiveClass = 'bg-slate-150'
const quizOptionActiveClass = 'border-primary bg-primary/10 text-navy'
const quizOptionInactiveClass = 'border-slate-150 text-slate-500 hover:border-primary hover:bg-primary/5'
const disabledInputClass = 'disabled:bg-slate-100 disabled:text-slate-500'

export function PublicJoinPage({ token, email: emailFromUrl, embedded = false }: Props) {
  const mounted = useIsMounted()
  const [step, setStep] = useState<Step>('loading')
  const [session, setSession] = useState<PBSession | null>(null)
  const [course, setCourse] = useState<Course | null>(null)
  const [attendee, setAttendee] = useState<ExistingAttendee | null>(null)
  const [loadError, setLoadError] = useState('')
  const [score, setScore] = useState(0)
  const [passingScore, setPassingScore] = useState(70)
  const [submitting, setSubmitting] = useState(false)

  // Registration form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [company, setCompany] = useState('')
  const [signed, setSigned] = useState(false)
  const [regError, setRegError] = useState('')

  // Quiz state
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [quizError, setQuizError] = useState('')

  const isExistingAttendee = attendee !== null

  useEffect(() => {
    void (async () => {
      try {
        const result = await getSessionByQrToken(token, emailFromUrl)
        if (!mounted.current) {return}
        if (!result) {
          setLoadError('Session not found. Verify the link is correct.')
          setStep('error')
          return
        }
        const s = result.session
        if (s.status !== 'active' && s.status !== 'checking') {
          setLoadError(
            s.status === 'draft'
              ? 'This session has not started yet. Wait for the instructor to begin.'
              : 'This session has already ended.'
          )
          setStep('error')
          return
        }
        setSession(s)
        const resolvedCourse = await resolveCourse(s)
        if (!mounted.current) {return}
        setCourse(resolvedCourse)
        setPassingScore(getCoursePassingScore(resolvedCourse))

        if (result.attendee) {
          const existing = result.attendee
          setAttendee(existing)
          setFirstName(existing.firstName)
          setLastName(existing.lastName)
          setEmail(existing.studentEmail)
          setPhone(existing.phone)
          setCompany(existing.company)
          // Pre-registered students who already checked in skip straight to done
          // (or quiz if the course has one and they haven't taken it).
          if (existing.checkedInAt) {
            setScore(existing.quizScore ?? 0)
            if (getCourseQuestions(resolvedCourse).length === 0 || existing.quizScore !== null) {
              setStep('done')
              return
            }
          }
        }
        setStep('register')
      } catch (err: unknown) {
        if (!mounted.current) {return}
        const status =
          typeof err === 'object' && err !== null && 'status' in err
            ? (err as { status: number }).status
            : 0
        if (status === 401 || status === 403) {
          setLoadError(
            'You do not have permission to access this session. Ask the instructor to verify the link configuration.'
          )
        } else {
          setLoadError('Could not load the session. Verify your connection and try again.')
        }
        setStep('error')
      }
    })()
  }, [token, emailFromUrl, mounted])

  const questions = useMemo(() => getCourseQuestions(course), [course])

  async function submitRegistration(finalStatus: AttendeeStatus, finalScore: number) {
    if (!session) {return}
    setSubmitting(true)
    try {
      // The public check-in endpoint ignores client-supplied status/score —
      // those are server-controlled. The QR token proves physical access.
      await createAttendee({
        sessionId: session.id,
        studentId: email.toLowerCase().trim(),
        qrToken: token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        company: company.trim(),
        quizAnswers: finalStatus === 'quiz-completed' ? answers : {},
      })
      setScore(finalScore)
      setStep('done')
    } catch {
      if (questions.length > 0) {
        setQuizError('Error submitting the diagnostic. Please try again.')
      } else {
        setRegError('Error completing registration. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRegister() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!firstName.trim() || !lastName.trim()) {
      setRegError('First and last name are required.')
      return
    }
    if (!emailRegex.test(email.trim())) {
      setRegError('Please enter a valid email address.')
      return
    }
    if (!phone.trim()) {
      setRegError('Phone number is required.')
      return
    }
    if (!signed) {
      setRegError('Please confirm your attendance with the digital signature.')
      return
    }
    setRegError('')
    if (questions.length === 0) {
      await submitRegistration('completed' as AttendeeStatus, 100)
      return
    }
    setStep('quiz')
  }

  async function handleQuizSubmit() {
    const unanswered = questions.filter((q) => answers[q.id] === undefined)
    if (unanswered.length > 0) {
      setQuizError(`Please answer all questions (${unanswered.length} unanswered).`)
      return
    }
    setQuizError('')
    const correct = questions.filter((q) => answers[q.id] === q.correctIndex).length
    const finalScore = questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0
    await submitRegistration('quiz-completed' as AttendeeStatus, finalScore)
  }

  const currentStep = step === 'quiz' || step === 'done' ? step : 'register'
  const activeStepIndex = stepOrder.indexOf(currentStep)

  return (
    <div className={cn('bg-gray-50 text-navy', embedded ? 'w-full' : 'min-h-screen')}>
      {!embedded && (
        <div className="border-b border-slate-150 bg-white px-5 py-4">
          <div className="mx-auto flex w-full max-w-2xl items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/images/logo/ub-logo-128.webp"
                alt="UBSafety"
                className="w-8 h-8 shrink-0 rounded-sm object-contain"
              />
              <span className="text-sm font-semibold tracking-wide text-navy">UBSafety</span>
            </div>
            <span className="text-xs text-primary font-medium">Student Registration</span>
          </div>
        </div>
      )}

      <div className={cn('mx-auto w-full max-w-2xl', embedded ? 'p-3 md:p-4' : 'p-4 md:p-8')}>
        {/* Loading */}
        {step === 'loading' && (
          <div className="mt-10 flex flex-col items-center gap-3 text-slate-muted">
            <Loader2 className="h-8 w-8 animate-spin text-amber-300" />
            <p className="text-sm">Loading session…</p>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <Card className="mx-auto mt-10 w-full max-w-lg border-red-500/30 bg-gray-900 text-stone-100">
            <CardContent className="flex flex-col items-center gap-4 pt-8 text-center">
              <XCircle className="h-12 w-12 text-red-400" />
              <p className="text-sm text-red-300">{loadError}</p>
            </CardContent>
          </Card>
        )}

        {/* Register / Quiz / Done */}
        {(step === 'register' || step === 'quiz' || step === 'done') && session && course && (
          <Card className="mx-auto w-full max-w-xl border-slate-150 bg-white text-navy shadow-sm">
            <CardHeader className="pb-3">
              <div className="mb-4 flex items-center gap-2">
                {stepOrder.map((stepName, index) => (
                  <div key={stepName} className="flex flex-1 items-center gap-2">
                    <div
                      className={cn(
                        'h-2 rounded-full transition-all',
                        index <= activeStepIndex ? progressDotActiveClass : progressDotInactiveClass
                      )}
                    />
                    {index < stepOrder.length - 1 && (
                      <div className={cn('h-px flex-1', index < activeStepIndex ? progressConnectorActiveClass : progressConnectorInactiveClass)} />
                    )}
                  </div>
                ))}
              </div>
              <CardTitle className="text-xl font-semibold text-navy">
                {step === 'register' && (isExistingAttendee ? 'Confirm attendance' : 'Participant data')}
                {step === 'quiz' && `Diagnostic – ${course.name}`}
                {step === 'done' && 'Registration complete!'}
              </CardTitle>
              <CardDescription className="text-slate-500">
                {step === 'register' && `${course.name} · ${session.locationName ?? session.location ?? '—'}`}
                {step === 'quiz' && `${questions.length} questions · Helps personalize the session`}
                {step === 'done' && 'Your results have been recorded.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
            {/* Step 1: Registration form */}
            {step === 'register' && (
              <div className="space-y-4">
                {regError && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                    <XCircle className="h-4 w-4 shrink-0" />
                    {regError}
                  </div>
                )}

                {isExistingAttendee && (
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                    <p className="text-sm font-medium text-navy">
                      Welcome back{firstName ? `, ${firstName}` : ''}
                    </p>
                    <p className="text-sm text-slate-500">
                      You are already registered for this session. Confirm your attendance below.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-sm text-slate-500 font-medium">First Name *</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      disabled={isExistingAttendee && Boolean(attendee?.firstName)}
                      className={cn(darkInputClass, disabledInputClass)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-slate-500 font-medium">Last Name *</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Smith"
                      disabled={isExistingAttendee && Boolean(attendee?.lastName)}
                      className={cn(darkInputClass, disabledInputClass)}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-slate-500 font-medium">Email *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="juan@empresa.com"
                    disabled={isExistingAttendee}
                    className={cn(darkInputClass, disabledInputClass)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-slate-500 font-medium">Phone *</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+52 (55) 0000-0000"
                    disabled={isExistingAttendee && Boolean(attendee?.phone)}
                    className={cn(darkInputClass, disabledInputClass)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-slate-500 font-medium">
                    Company <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Company name"
                    disabled={isExistingAttendee && Boolean(attendee?.company)}
                    className={cn(darkInputClass, disabledInputClass)}
                  />
                </div>
                <div className="rounded-lg border border-slate-150 bg-slate-50 p-4">
                  <p className="mb-3 text-sm font-medium text-navy">Digital signature</p>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={signed} onChange={(e) => setSigned(e.target.checked)} className="mt-0.5 accent-primary" />
                    <span className="text-sm text-slate-500">
                      I confirm my attendance and agree to participate in this training session.
                      I understand my information will be used to generate my competency certificate.
                    </span>
                  </label>
                </div>
                <div className="flex justify-end pt-1">
                  <Button onClick={handleRegister} disabled={submitting} className={cn(primaryButtonClass, 'px-6')}>
                    {submitting ? 'Sending…' : questions.length === 0 ? 'Confirm attendance' : 'Next: Diagnostic →'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Quiz */}
            {step === 'quiz' && (
              <div className="space-y-5">
                {quizError && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                    <XCircle className="h-4 w-4 shrink-0" />
                    {quizError}
                  </div>
                )}
                {questions.map((q, qi) => (
                  <div key={q.id} className="space-y-2">
                    <p className="text-sm font-medium text-navy">{qi + 1}. {q.text}</p>
                    <div className="space-y-1.5 pl-3">
                      {q.options.map((opt, oi) => (
                        <label
                          key={`${q.id}-${opt}`}
                          className={cn(
                            'flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors text-sm',
                            answers[q.id] === oi
                              ? quizOptionActiveClass
                              : quizOptionInactiveClass
                          )}
                        >
                          <input
                            type="radio"
                            name={q.id}
                            value={oi}
                            checked={answers[q.id] === oi}
                            onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: oi }))}
                            className="accent-primary"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <Button onClick={handleQuizSubmit} disabled={submitting} className={cn(primaryButtonClass, 'px-6')}>
                    <CheckCircle2 className="h-4 w-4" />
                    {submitting ? 'Sending…' : 'Submit Diagnostic'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Completion summary */}
            {step === 'done' && (
              <div className="text-center space-y-4 py-4">
                <div className={cn('mx-auto flex h-16 w-16 items-center justify-center rounded-full', score >= passingScore ? 'bg-green-100' : 'bg-amber-100')}>
                  {score >= passingScore ? (
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-8 w-8 text-amber-600" />
                  )}
                </div>
                <div>
                  <p className="text-2xl font-bold text-primary">{score}%</p>
                  <p className="mt-1 text-sm text-slate-500">
                    {score >= passingScore
                      ? 'Excellent score! You are well prepared for this course.'
                      : 'There are some topics to review during today\'s session.'}
                  </p>
                </div>
                <div className="space-y-1 rounded-lg border border-slate-150 bg-slate-50 p-4 text-left text-sm">
                  <p className="font-medium text-navy">Attendance confirmed ✓</p>
                  <p className="text-slate-500">
                    Your check-in is recorded. Your certificate will be emailed to you
                    automatically once the session ends.
                  </p>
                </div>
              </div>
            )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
